import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  DirectionsBoat,
  Flight,
  Home,
  ShoppingCart,
  CheckCircle,
  Timeline,
  Security,
  Support,
} from '@mui/icons-material';

const services = [
  {
    id: 'maritime',
    title: 'Groupage Maritime',
    icon: DirectionsBoat,
    description: 'Service de groupage maritime professionnel entre la France et la Côte d\'Ivoire.',
    image: require('../assets/images/slide1.jpg'),
    processSteps: [
      'Nous faisons l\'enlèvement de vos marchandises chez votre fournisseur ou à votre domicile',
      'Nous les mettons dans notre conteneur',
      'En deux à trois semaines vous récupérez votre colis à votre entrepôt à Abidjan ou nous vous livrons'
    ],
    features: [
      'Enlèvement à domicile en France',
      'Conteneur dédié et sécurisé',
      'Délai de 2-3 semaines',
      'Livraison à Abidjan',
      'Service porte-à-porte',
      'Suivi en temps réel'
    ],
    benefits: [
      'Économies sur les frais de transport',
      'Gestion complète de A à Z',
      'Flexibilité de livraison',
      'Service personnalisé'
    ]
  },
  {
    id: 'air',
    title: 'Groupage Aérien',
    icon: Flight,
    description: 'Transport aérien rapide et sécurisé de vos colis',
    image: require('../assets/images/slide3.jpg'),
    features: [
      'Livraison express',
      'Service prioritaire',
      'Tracking avancé',
      'Emballage sécurisé',
      'Dédouanement rapide',
      'Service client 24/7'
    ],
    benefits: [
      'Rapidité d\'acheminement',
      'Fiabilité maximale',
      'Flexibilité des options',
      'Support dédié'
    ]
  },
  {
    id: 'moving',
    title: 'Déménagement International',
    icon: Home,
    description: 'Solutions complètes pour votre déménagement entre la France et la Côte d\'Ivoire',
    image: require('../assets/images/slide2.jpg'),
    features: [
      'Emballage professionnel',
      'Transport sécurisé',
      'Stockage temporaire',
      'Assurance complète',
      'Gestion douanière',
      'Installation à destination'
    ],
    benefits: [
      'Tranquillité d\'esprit',
      'Service clé en main',
      'Expertise internationale',
      'Accompagnement personnalisé'
    ]
  },
  {
    id: 'shopping',
    title: 'Achat International',
    icon: ShoppingCart,
    description: 'Service d\'achat et d\'importation depuis la Chine vers la Côte d\'Ivoire',
    image: require('../assets/images/slide4.jpg'),
    features: [
      'Sourcing fournisseurs',
      'Contrôle qualité',
      'Négociation prix',
      'Consolidation colis',
      'Transport international',
      'Livraison finale'
    ],
    benefits: [
      'Gain de temps',
      'Réduction des coûts',
      'Sécurité des transactions',
      'Expertise locale'
    ]
  }
];

const ServiceFeatures = () => (
  <Grid container spacing={4} sx={{ mt: 4 }}>
    <Grid item xs={12} md={3}>
      <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
        <Timeline sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Suivi en Temps Réel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suivez vos envois à chaque étape
        </Typography>
      </Card>
    </Grid>
    <Grid item xs={12} md={3}>
      <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
        <Security sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Sécurité Garantie
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Protection optimale de vos biens
        </Typography>
      </Card>
    </Grid>
    <Grid item xs={12} md={3}>
      <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
        <Support sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Support 24/7
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assistance personnalisée
        </Typography>
      </Card>
    </Grid>
    <Grid item xs={12} md={3}>
      <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
        <CheckCircle sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Qualité Certifiée
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Standards internationaux
        </Typography>
      </Card>
    </Grid>
  </Grid>
);

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom>
            Nos Services
          </Typography>
          <Typography variant="h5">
            Des solutions logistiques complètes pour vos besoins internationaux
          </Typography>
        </Container>
      </Box>

      {/* Common Features */}
      <Container maxWidth="lg">
        <ServiceFeatures />
      </Container>

      {/* Detailed Services */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {services.map((service, index) => {
          const IconComponent = service.icon;
          return (
            <Box key={service.id} id={service.id} sx={{ mb: 8 }}>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ 
                      flexGrow: 0, 
                      pb: 0,
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 1
                      }}>
                        <IconComponent sx={{ 
                          fontSize: 40, 
                          mr: 2,
                          color: 'white'
                        }} />
                        <Typography variant="h4" component="h2">
                          {service.title}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardMedia
                      component="img"
                      height={service.id === 'maritime' ? "300" : "400"}
                      image={service.image}
                      alt={service.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1, pt: service.id === 'maritime' ? 2 : 2 }}>
                      <Typography 
                        variant="body1" 
                        paragraph
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          color: 'text.primary',
                          mb: service.id === 'maritime' ? 2 : 3
                        }}
                      >
                        {service.description}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        {service.processSteps && service.processSteps.map((step, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              mb: 2,
                              backgroundColor: 'background.paper',
                              borderRadius: 1,
                              p: 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              '&:hover': {
                                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                mr: 2,
                                flexShrink: 0
                              }}
                            >
                              {idx + 1}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '1rem',
                                color: 'text.primary',
                                lineHeight: 1.5,
                                pt: 0.3
                              }}
                            >
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 3
                        }}
                      >
                        <Button 
                          variant="contained" 
                          size="large"
                          sx={{
                            px: 4,
                            py: 1.5,
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          Demander un Devis
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      maxHeight: '600px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="fullWidth"
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Tab 
                        label="Caractéristiques" 
                        sx={{ 
                          fontWeight: 600,
                          py: 2,
                        }}
                      />
                      <Tab 
                        label="Avantages" 
                        sx={{ 
                          fontWeight: 600,
                          py: 2,
                        }}
                      />
                    </Tabs>
                    <Box 
                      sx={{ 
                        flexGrow: 1,
                        overflow: 'auto',
                        p: 3,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'primary.main',
                          borderRadius: '4px',
                        },
                      }}
                    >
                      {activeTab === 0 ? (
                        <List dense>
                          {service.features.map((feature, idx) => (
                            <React.Fragment key={idx}>
                              <ListItem>
                                <ListItemIcon>
                                  <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={feature}
                                  primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                  }}
                                />
                              </ListItem>
                              {idx < service.features.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      ) : (
                        <List dense>
                          {service.benefits.map((benefit, idx) => (
                            <React.Fragment key={idx}>
                              <ListItem>
                                <ListItemIcon>
                                  <CheckCircle color="secondary" sx={{ fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={benefit}
                                  primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                  }}
                                />
                              </ListItem>
                              {idx < service.benefits.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
              {index < services.length - 1 && (
                <Divider sx={{ my: 6 }} />
              )}
            </Box>
          );
        })}
      </Container>
    </Box>
  );
};

export default Services;
